import { useSearchParams } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { useSession } from '@/session/SessionProvider';
import { Button } from '@/components/ui/Button';
import { useLogout } from '@/lib/queries';

const MESSAGES: Record<string, { title: string; body: string }> = {
  NOT_IN_GUILD: {
    title: 'You are not in the Spring Side Network server',
    body: 'The Development Hub is only available to members of the Spring Side Network Discord server. Join the server, then sign in again.',
  },
  NO_MAPPED_ROLE: {
    title: 'You do not have a Development role',
    body: 'Your Discord account was recognised, but it does not hold a Development Team, Senior Developer or Development Director role. A Development Director can grant you access.',
  },
  DEACTIVATED: {
    title: 'Your account has been deactivated',
    body: 'A Development Director has deactivated your Development Hub account. Contact them if you think this is a mistake.',
  },
};

/**
 * The screen for someone who authenticated but may not come in (§3).
 *
 * It says exactly which of the three reasons applies and what to do about it —
 * a bare "access denied" leaves the person with no next step.
 */
export function NoAccessPage() {
  const [params] = useSearchParams();
  const { denial } = useSession();
  const logout = useLogout();

  const reason = denial?.reason ?? params.get('reason') ?? 'NO_MAPPED_ROLE';
  const content = MESSAGES[reason] ?? MESSAGES['NO_MAPPED_ROLE']!;

  return (
    <div className="grid min-h-dvh place-items-center bg-canvas px-4 py-10">
      <div className="w-full max-w-md rounded-[--radius-panel] border border-border bg-surface p-6 text-center shadow-panel">
        <div className="mx-auto mb-4 flex size-11 items-center justify-center rounded-full bg-warning-soft text-warning-tone">
          <ShieldCheck aria-hidden className="size-5" />
        </div>

        <h1 className="text-base font-semibold text-text">{content.title}</h1>
        <p className="mt-2 text-sm text-text-muted">{denial?.message ?? content.body}</p>

        <div className="mt-6 flex justify-center gap-2">
          <Button
            variant="secondary"
            loading={logout.isPending}
            onClick={() =>
              logout.mutate(undefined, {
                onSuccess: () => {
                  window.location.href = '/signin';
                },
              })
            }
          >
            Sign out
          </Button>
          <Button variant="primary" onClick={() => window.location.reload()}>
            Try again
          </Button>
        </div>
      </div>
    </div>
  );
}
